import { ColliState, ColliStateName, ColliTrigger, ColliTriggerName } from '@frontend/colli/types';
import { PossibleTrigger } from '@frontend/workflow/types';
import type { ReactNode } from 'react';

import { PossibleTriggerDisplay, PossibleTriggerGroup, PossibleTriggerLabels, fallbackPossibleTriggerDisplay } from '../../../workflow/possible-trigger-labels';

interface ColliPossibleTriggerRule {
    trigger: ColliTriggerName;
    nextState?: ColliStateName | null;
    group: Exclude<PossibleTriggerGroup, 'other'>;
    label: ReactNode;
}

/**
 * Specific (trigger + next_state) rules first, then any-next-state action rules.
 */
const COLLI_POSSIBLE_TRIGGER_RULES: ColliPossibleTriggerRule[] = [
    {
        trigger: ColliTriggerName.COLLI_PROCESS,
        nextState: ColliStateName.COLLI_DROP_OFF_PENDING,
        group: 'action',
        label: PossibleTriggerLabels.MARK_READY_FOR_DROP_OFF
    },
    {
        trigger: ColliTriggerName.COLLI_DROPPED_OFF,
        nextState: ColliStateName.COLLI_PICK_UP_PENDING,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_DROPPED_OFF
    },
    {
        trigger: ColliTriggerName.COLLI_PICKED_UP,
        nextState: ColliStateName.COLLI_COMPLETED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP
    },
    {
        trigger: ColliTriggerName.COLLI_PICKED_UP,
        nextState: ColliStateName.COLLI_IN_TRANSIT,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP_IN_TRANSIT
    },
    {
        trigger: ColliTriggerName.COLLI_PICKED_UP,
        nextState: ColliStateName.COLLI_DROP_OFF_PENDING,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP_AWAITING_DROP_OFF
    },
    {
        trigger: ColliTriggerName.COLLI_PICKED_UP,
        nextState: ColliStateName.COLLI_PICK_UP_DONE,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICKED_UP_PICK_UP_DONE
    },
    {
        trigger: ColliTriggerName.COLLI_CANCELLED,
        nextState: ColliStateName.COLLI_CANCELLED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_CANCELLED
    },
    {
        trigger: ColliTriggerName.COLLI_FAILED,
        nextState: ColliStateName.COLLI_FAILED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_FAILED
    },
    {
        trigger: ColliTriggerName.COLLI_LOST,
        nextState: ColliStateName.COLLI_LOST,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_LOST
    },
    {
        trigger: ColliTriggerName.COLLI_PROCESSED,
        nextState: ColliStateName.COLLI_COMPLETED,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_COMPLETED
    },
    {
        trigger: ColliTriggerName.COLLI_PROCESSED,
        nextState: ColliStateName.COLLI_PICK_UP_DONE,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_PICK_UP_DONE
    },
    {
        trigger: ColliTriggerName.COLLI_PROCESSED_WITH_FAILURES,
        nextState: ColliStateName.COLLI_COMPLETED_WITH_FAILURES,
        group: 'status_update',
        label: PossibleTriggerLabels.MARK_COMPLETED_WITH_FAILURES
    },
    {
        trigger: ColliTriggerName.COLLI_PICK_UP,
        group: 'action',
        label: PossibleTriggerLabels.PICK_UP
    },
    {
        trigger: ColliTriggerName.COLLI_DROP_OFF,
        group: 'action',
        label: PossibleTriggerLabels.DROP_OFF
    },
    {
        trigger: ColliTriggerName.COLLI_CANCEL,
        group: 'action',
        label: PossibleTriggerLabels.CANCEL
    },
    {
        trigger: ColliTriggerName.COLLI_COMPLETE,
        group: 'action',
        label: PossibleTriggerLabels.COMPLETE
    }
];

export function getColliPossibleTriggerDisplay(
    possibleTrigger: PossibleTrigger<ColliTriggerName | string, ColliStateName | string | null>
): PossibleTriggerDisplay {
    const match = COLLI_POSSIBLE_TRIGGER_RULES.find((rule) => {
        if (rule.trigger !== possibleTrigger.trigger) {
            return false;
        }
        if (rule.nextState === undefined) {
            return true;
        }
        return rule.nextState === possibleTrigger.next_state;
    });

    if (match) {
        return { group: match.group, display: match.label };
    }

    const triggerDisplay = ColliTrigger.getByValue(possibleTrigger.trigger as ColliTriggerName)?.displayName ?? possibleTrigger.trigger;
    const nextStateDisplay =
        possibleTrigger.next_state == null
            ? null
            : (ColliState.getByValue(possibleTrigger.next_state as ColliStateName)?.displayName ?? possibleTrigger.next_state);

    return {
        group: 'other',
        display: fallbackPossibleTriggerDisplay(triggerDisplay, nextStateDisplay)
    };
}
